import React, { useEffect, useState, useCallback } from "react";
import { Plus, Wrench, Pencil, Trash, Clock, Warning } from "@phosphor-icons/react";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { AddInterventionModal } from "./AddInterventionModal";
import { getPrimaryTicketSupplier } from "../../shared/ticketSuppliers";
import {
  InterventionDTO,
  getInterventionStatusChipClass,
  isInterventionOverdue,
} from "../intervention/interventionConstants";

interface InterventionsSectionProps {
  ticket: any;
  onChanged?: () => void;
}

const formatDate = (raw: string | null | undefined): string => {
  if (!raw) return "Geen datum";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return "Geen datum";
  // Show date + local time (browser TZ; users are in Brussels).
  return d.toLocaleString("nl-BE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const InterventionsSection: React.FC<InterventionsSectionProps> = ({
  ticket,
  onChanged,
}) => {
  const { session } = useSession();
  const [items, setItems] = useState<InterventionDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<InterventionDTO | null>(null);

  const fetchInterventions = useCallback(async () => {
    if (!ticket?.id) return;
    setLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/ticket/${ticket.id}/interventions`,
        { method: "GET" },
        session,
      );
      if (!response.ok) {
        throw new Error(`Failed: ${response.status}`);
      }
      const data = await response.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("[INTERVENTION] Fetch failed:", error);
      toast.error("Kon interventies niet laden");
    } finally {
      setLoading(false);
    }
  }, [ticket?.id, session]);

  useEffect(() => {
    fetchInterventions();
  }, [fetchInterventions]);

  const handleDelete = async (id: number) => {
    if (!confirm("Weet je zeker dat je deze interventie wilt verwijderen?")) {
      return;
    }
    try {
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/interventions/${id}`,
        { method: "DELETE" },
        session,
      );
      if (!response.ok) {
        throw new Error(`Failed: ${response.status}`);
      }
      toast.success("Interventie verwijderd");
      await fetchInterventions();
      onChanged?.();
    } catch (error) {
      console.error("[INTERVENTION] Delete failed:", error);
      toast.error("Verwijderen mislukt");
    }
  };

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (intervention: InterventionDTO) => {
    setEditing(intervention);
    setModalOpen(true);
  };

  const handleSaved = async () => {
    await fetchInterventions();
    onChanged?.();
  };

  return (
    <div className="border-border rounded-lg border bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wrench size={20} className="text-moss-primary" />
          <h3 className="text-neutral-primary text-sm font-semibold">
            Interventies
          </h3>
          {items.length > 0 && (
            <span className="text-neutral-secondary-mid text-xs">
              ({items.length})
            </span>
          )}
        </div>
        <Button
          size="sm"
          onClick={openCreate}
          className="bg-moss-primary hover:bg-moss-primary/90 text-white"
        >
          <Plus size={16} className="mr-1" />
          Interventie
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <p className="text-neutral-secondary-mid py-4 text-center text-sm">
          Nog geen interventies. Voeg er één toe om opvolging te starten.
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((intervention) => {
            const overdue = isInterventionOverdue(
              intervention.status,
              intervention.plannedDate,
            );
            return (
              <li
                key={intervention.id}
                className="border-border hover:border-neutral-300 group rounded-md border p-3 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`rounded border px-2 py-0.5 text-xs font-medium ${getInterventionStatusChipClass(
                          intervention.status,
                        )}`}
                      >
                        {intervention.status}
                      </span>
                      <span className="border-neutral-300 bg-neutral-50 text-neutral-secondary-mid rounded border px-2 py-0.5 text-xs">
                        {intervention.type}
                      </span>
                      {overdue && (
                        <span className="flex items-center gap-1 rounded border border-red-300 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                          <Warning size={12} weight="fill" />
                          Te laat
                        </span>
                      )}
                    </div>

                    <div className="text-neutral-primary flex flex-wrap items-center gap-3 text-xs">
                      <span className="flex items-center gap-1">
                        <Clock size={14} className="text-neutral-secondary-mid" />
                        {formatDate(intervention.plannedDate)}
                      </span>
                      {intervention.supplier?.name && (
                        <span className="flex items-center gap-1">
                          <Wrench size={14} className="text-neutral-secondary-mid" />
                          {intervention.supplier.name}
                        </span>
                      )}
                    </div>

                    {intervention.description && (
                      <p className="text-neutral-secondary-mid mt-1 text-xs whitespace-pre-wrap">
                        {intervention.description}
                      </p>
                    )}
                  </div>

                  <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => openEdit(intervention)}
                      aria-label="Interventie bewerken"
                    >
                      <Pencil size={14} />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleDelete(intervention.id)}
                      aria-label="Interventie verwijderen"
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <AddInterventionModal
        isOpen={modalOpen}
        ticketId={ticket?.id}
        intervention={editing}
        ticketSupplier={(() => {
          const primarySupplier = getPrimaryTicketSupplier(ticket);
          return primarySupplier
            ? { id: primarySupplier.id, name: primarySupplier.name }
            : null;
        })()}
        onClose={() => setModalOpen(false)}
        onSaved={handleSaved}
      />
    </div>
  );
};
